import { POSTS, postBySlug } from "@/content/posts";
import { stateFromSlug } from "@/lib/states";

export const SITE_URL = "https://arcadeops.net";
const BRAND = "Arcade Ops";

const PAGES = {
  "/": {
    title: "Arcade Ops — The Placement Engine for Amusement & Skill-Game Operators",
    description: "Verified owner contacts for every eligible venue in your state plus approve-before-send AI calls and email. $399/mo per state, 14-day trial (card required). Place Fortune Games and AOG Coin machines.",
  },
  "/pricing": {
    title: "Pricing — Scout $149, Full Operator $399/state, Placement Engine | Arcade Ops",
    description: "Scout $149/mo for one state, Full Operator $399/mo per state (+$199 per additional state), Placement Engine done-for-you $1,500/mo + $250 per placement billed up front. 14-day trial, card required. Annual billing: 2 months free.",
  },
  "/states": {
    title: "Skill Game Machines & Gaming Machines for C-Stores by State — Coverage in All 50 States + DC | Arcade Ops",
    description: "Place skill game machines, pre-reveal games, sweepstakes game kiosks and ATM kiosks in c-stores, gas stations and bars. See verified venue owners in every state. $399/mo per state, 14-day trial (card required).",
  },
  "/about": {
    title: "About Arcade Ops — Norcross, GA",
    description: "Arcade Ops is the placement engine for amusement and skill-game operators, founded by Aziz Damani. Headquartered at 5675 Jimmy Carter Blvd, Suite 725, Norcross, GA 30071.",
  },
  "/aog-coin": {
    title: "AOG Sweeps Game Kiosk — Free Sweepstakes Game Kiosk for Your Counter | Arcade Ops",
    description: "Get a free AOG Sweeps game kiosk, a 21+ sweepstakes game platform, for your counter — nothing to buy, nothing to install yourself. For vape shops, gas stations, c-stores, grocers and bars in eligible states.",
  },
  "/fleet": {
    title: "Fleet & Fuel-Jobber Partnership — $0 Upfront Fortune Games | Arcade Ops",
    description: "Fortune Games machines placed free across your fuel fleet. We install, service and insure — you keep a monthly revenue split, paid on the 1st of every month.",
  },
  "/saas": {
    title: "Database + Outreach Platform for Machine Operators — Features & ROI | Arcade Ops",
    description: "Owner-verified phones and emails for c-stores, gas stations, bars and vape shops, state by state, with one-click call and email, team seats and done-for-you campaigns. $399/mo per state after a 14-day trial (card required).",
  },
  "/demo": {
    title: "Live Demo — Owner-Verified Leads in Real Time | Arcade Ops",
    description: "Live counters straight from the Arcade Ops database: verified owner leads for gaming, convenience-store and bar operators by state. Book a 15-minute demo.",
  },
  "/aog": {
    title: "AOG Coin Sign-Up Kiosk for Your Store | Arcade Ops",
    description: "Sign up your bar, convenience store or gas station for the AOG sign-up kiosk tablet — delivered to your store with marketing materials. Eligible states only, 21+.",
    canonical: "/aog-coin",
  },
  "/sponsor": {
    title: "Sponsor a Blast — Reach Gaming & Retail Operators | Arcade Ops",
    description: "Put your brand in front of vetted gaming-venue and retail operators with sponsored email and SMS blasts sent through the Arcade Ops proprietary lead database.",
  },
  "/social": {
    title: "Arcade Ops Social Kit — Share Our Story",
    description: "Ready-to-post copy, images and links to tell the world about Arcade Ops. Everything you need to share us on LinkedIn, X, Facebook, Instagram and TikTok.",
  },
  "/ads/fortune": {
    title: "Fortune Games $3,500 Skill-Based Cabinet · Free Delivery & Install on 2+ | Arcade Ops",
    description: "43\" Fortune Games cabinet, 6-game pack per cabinet, $3,500 per cabinet. FREE delivery & install on 2 or more cabinets ($500 for a single cabinet). 1-year parts and labor, no revenue share — 100% of the profit stays with the store.",
  },
  "/fortune": {
    title: "Fortune Skill Game Machines — $3,500 All In, 6-Game Pack, Newest Game Fortune Treasures | Arcade Ops",
    description: "Fortune 43\" skill game cabinet for c-stores, gas stations and bars: $3,500 all in with a 6-game pack (newest game Fortune Treasures), free delivery and install on 2+ cabinets, 1-year parts and labor warranty, no revenue share. ATM kiosks $6,000.",
  },
  "/blog": {
    title: "Blog — Skill Games, Pre-Reveal Games & Sweepstakes Game Kiosks for Stores | Arcade Ops",
    description: "Plain guides for store owners and route operators: how skill game placement works, what a pre-reveal game is, Fortune cabinets vs. other skill game machines, the AOG Sweeps game kiosk and ATM kiosks.",
  },
  "/404.html": { title: "Page not found | Arcade Ops", description: "That page doesn't exist. Find Fortune skill game cabinets, the AOG Sweeps game kiosk, pricing and coverage by state.", noindex: true },
  "/privacy": {
    title: "Privacy Policy | Arcade Ops",
    description: "How Arcade Ops collects, uses and protects business contact data, website analytics and communication preferences, and how to reach us about your data.",
  },
  "/terms": {
    title: "Terms of Service | Arcade Ops",
    description: "The terms governing use of the Arcade Ops platform, subscriptions and billing, lead data licensing, and the outreach tools available to subscribers.",
  },
  "/sms-consent": {
    title: "SMS Consent & Terms | Arcade Ops",
    description: "How SMS opt-in works with Arcade Ops: what you'll receive, message frequency, STOP and HELP keywords, carrier rates and how opt-outs are honoured.",
  },
  "/login": { title: "Sign In | Arcade Ops", description: "Sign in to your Arcade Ops account.", noindex: true },
  "/register": { title: "Create Account | Arcade Ops", description: "Create your Arcade Ops account.", noindex: true, canonical: "/login" },
  "/verify-email": { title: "Verify Email | Arcade Ops", noindex: true },
  "/reset-password": { title: "Reset Password | Arcade Ops", noindex: true },
  "/redeem": { title: "Redeem Code | Arcade Ops", noindex: true },
  "/cancel-trial": { title: "Cancel Trial | Arcade Ops", noindex: true },
};

const NOINDEX_PREFIXES = ["/app", "/portal", "/fortune/", "/preview/", "/r"];

// Public FAQs (rendered on the page AND emitted as FAQPage JSON-LD — one source so the two never drift).
export const STATES_FAQ = [
  ["Which skill game machines and gaming machines can a c-store or gas station place?", "Most independent stores choose between a skill game cabinet they buy outright (the Fortune 43-inch cabinet is $3,500 all in with a 6-game pack), a free AOG Sweeps game kiosk — a 21+ sweepstakes game platform offered in eligible states — and an ATM kiosk ($6,000, sold in any state). Which machines are permitted depends on your state and city."],
  ["Is Arcade Ops software available in every state?", "Yes. The verified venue database and approve-before-send outreach work in all 50 states and DC at the same price per state. Placement products are a separate question: Fortune cabinets are sold for use where permitted by law and the AOG Sweeps game kiosk is offered only in eligible states."],
  ["How do route operators use the state pages?", "Each state page shows how many venues, owner emails and validated phones are ready today — c-stores, gas stations, bars, laundromats and smoke shops — so an amusement machine route operator can size a territory before starting a trial."],
  ["What does it cost per state?", "Full Operator is $399/mo for your first state and $199/mo for each additional state, with a 14-day trial (card required, nothing charged today). Scout is $149/mo for one state."],
];

export const AOG_EXCLUDED_STATES = ["California", "Connecticut", "Delaware", "District of Columbia", "Georgia", "Idaho", "Illinois", "Indiana", "Kentucky", "Louisiana", "Maryland", "Maine", "Michigan", "Mississippi", "Montana", "Nevada", "New Jersey", "New York", "Oklahoma", "Tennessee", "Washington", "West Virginia"];

export function stateFaq(state) {
  const georgia = state === "Georgia";
  const aogOut = AOG_EXCLUDED_STATES.includes(state);
  const products = georgia
    ? `Fortune skill game cabinets and the AOG Sweeps game kiosk are not offered in ${state}. ATM kiosks ($6,000) are sold in any state.`
    : aogOut
      ? `Fortune skill game cabinets are sold for use where permitted by law, so confirm your local rules. The AOG Sweeps game kiosk, a 21+ sweepstakes game platform, is not currently offered in ${state}. ATM kiosks ($6,000) are sold in any state.`
      : `Fortune skill game cabinets ($3,500 all in, 6-game pack) are sold for use where permitted by law. The AOG Sweeps game kiosk, a 21+ sweepstakes game platform, is offered in ${state} for eligible stores. ATM kiosks ($6,000) are sold in any state.`;
  return [
    [`Can I use Arcade Ops to place skill game machines in ${state}?`, `Yes. The ${state} database lists c-stores, gas stations, bars, laundromats and smoke shops with verified owner phones and emails, and the approve-before-send calls and emails book the owner conversations for you. The Arcade Ops platform works in ${state} today at $399/mo.`],
    [`Which gaming machines for c-stores and gas stations are offered in ${state}?`, products],
    [`Does the ${state} price differ from other states?`, `No. Every state is $399/mo for your first state and $199/mo for each additional state, with a 14-day trial (card required). ${state} venue counts update as records are verified.`],
  ];
}

const faqLd = (qa) => ({ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: qa.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) });
const breadcrumbLd = (items) => ({ "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: items.map(([name, path], i) => ({ "@type": "ListItem", position: i + 1, name, item: SITE_URL + path })) });

export const PRICING_FAQ = [
  ["What counts as a state?", "One U.S. state's worth of eligible venues with verified owner contacts. Your first state is $399/mo on Full Operator; each additional state is $199/mo."],
  ["How does the trial work?", "14 days of Full Operator on one state. A card is required to start; it auto-renews at $399/mo for that state unless you cancel from your portal before the trial ends."],
  ["Does anything go out automatically?", "No. Lucky AI calls and email are approve-before-send: you pick the group, preview exactly who will be contacted, and approve the batch. Kill switches hold everything."],
  ["Can I switch plans?", "Yes — upgrade, add states or move to the Placement Engine any time; the change is prorated on your next invoice."],
];

const ORGANIZATION = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: BRAND,
  url: SITE_URL + "/",
  logo: `${SITE_URL}/brand/arcadeops-logo.png`,
  description: "The placement engine for amusement and skill-game operators: verified owner contacts for every eligible venue in a state, approve-before-send AI calls and email, Fortune Games and AOG Coin placements.",
  areaServed: "US",
  founder: { "@type": "Person", name: "Aziz Damani", jobTitle: "Founder" },
  parentOrganization: { "@type": "Organization", name: "Damani Holdings" },
  address: { "@type": "PostalAddress", streetAddress: "5675 Jimmy Carter Blvd, Suite 725", addressLocality: "Norcross", addressRegion: "GA", postalCode: "30071", addressCountry: "US" },
  contactPoint: [{ "@type": "ContactPoint", contactType: "sales", telephone: "+1-325-221-4234", email: "sales@arcadeops.net", availableLanguage: "en" }],
};

const FORTUNE_PRODUCT = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: 'Fortune Games 43" Skill Game Cabinet',
  description: "43-inch Fortune Games cabinet with 6-game pack per cabinet, game pack included. $3,500 per cabinet. FREE delivery & install on 2 or more cabinets ($500 for a single cabinet). 1-year parts and labor warranty, no revenue share — 100% of the profit stays with the store.",
  image: [`${SITE_URL}/api/fortune/flyer.png`],
  brand: { "@type": "Brand", name: "Fortune Games" },
  sku: "FG-43-5G",
  category: "Amusement & skill game cabinets",
  offers: {
    "@type": "Offer",
    url: `${SITE_URL}/ads/fortune`,
    price: "3500.00",
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
    itemCondition: "https://schema.org/NewCondition",
    warranty: { "@type": "WarrantyPromise", durationOfWarranty: { "@type": "QuantitativeValue", value: 1, unitCode: "ANN" } },
    seller: { "@id": `${SITE_URL}/#organization`, "@type": "Organization", name: BRAND },
  },
};

const LOCAL_BUSINESS = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}/#localbusiness`,
  name: BRAND,
  url: SITE_URL + "/",
  image: `${SITE_URL}/brand/arcadeops-logo.png`,
  telephone: "+1-325-221-4234",
  email: "sales@arcadeops.net",
  priceRange: "$$",
  description: "Skill game machines, pre-reveal games, sweepstakes game kiosks and ATM kiosks for c-stores, gas stations and bars — and the placement engine route operators use to find venue owners.",
  address: ORGANIZATION.address,
  areaServed: "US",
  parentOrganization: { "@id": `${SITE_URL}/#organization` },
};

const ATM_PRODUCT = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "ATM Kiosk",
  description: "ATM kiosk for convenience stores, gas stations and bars. $6,000 each, flat price at any quantity, on-site install about 5 days after order, sold in any state.",
  brand: { "@type": "Brand", name: BRAND },
  category: "ATM kiosks",
  offers: { "@type": "Offer", url: `${SITE_URL}/fortune#atm`, price: "6000.00", priceCurrency: "USD", availability: "https://schema.org/InStock", itemCondition: "https://schema.org/NewCondition",
    seller: { "@id": `${SITE_URL}/#organization`, "@type": "Organization", name: BRAND } },
};

const postLd = (p) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: p.title,
  description: p.seo_description || p.excerpt,
  datePublished: p.published_at,
  dateModified: p.updated_at || p.published_at,
  wordCount: p.body_md.split(/\s+/).length,
  keywords: (p.tags || []).join(", "),
  mainEntityOfPage: `${SITE_URL}/blog/${p.slug}`,
  image: [`${SITE_URL}/api/public/og/home.png?v=20261007`],
  author: { "@type": "Organization", name: BRAND, url: SITE_URL + "/" },
  publisher: { "@type": "Organization", name: BRAND, url: SITE_URL + "/", logo: { "@type": "ImageObject", url: `${SITE_URL}/brand/arcadeops-logo.png` } },
});

const BLOG_LD = {
  "@context": "https://schema.org",
  "@type": "Blog",
  "@id": `${SITE_URL}/blog#blog`,
  name: "Arcade Ops Blog",
  url: `${SITE_URL}/blog`,
  description: PAGES["/blog"].description,
  publisher: { "@id": `${SITE_URL}/#organization` },
  blogPost: POSTS.map((p) => ({ "@type": "BlogPosting", headline: p.title, url: `${SITE_URL}/blog/${p.slug}`, datePublished: p.published_at })),
};

const JSON_LD = {
  "/": [ORGANIZATION, LOCAL_BUSINESS],
  "/about": LOCAL_BUSINESS,
  "/ads/fortune": FORTUNE_PRODUCT,
  "/fortune": [FORTUNE_PRODUCT, ATM_PRODUCT],
  "/pricing": faqLd(PRICING_FAQ),
  "/states": faqLd(STATES_FAQ),
  "/blog": BLOG_LD,
};

export function jsonLdFor(pathname) {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  if (JSON_LD[path]) return JSON_LD[path];
  const blog = /^\/blog\/([a-z0-9-]+)$/.exec(path);
  if (blog) {
    const p = postBySlug(blog[1]);
    return p ? [postLd(p), breadcrumbLd([["Home", "/"], ["Blog", "/blog"], [p.title, `/blog/${p.slug}`]])] : null;
  }
  const st = /^\/states\/([a-z0-9-]+)$/.exec(path);
  if (st) {
    const state = stateFromSlug(st[1]);
    return state ? [faqLd(stateFaq(state)), breadcrumbLd([["Home", "/"], ["States", "/states"], [state, path]])] : null;
  }
  return null;
}

export function metaFor(pathname) {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const page = PAGES[path];
  if (page) return { ...page, canonical: SITE_URL + (page.canonical || path) };
  const blog = /^\/blog\/([a-z0-9-]+)$/.exec(path);
  if (blog) {
    const p = postBySlug(blog[1]);
    if (p) return { title: `${p.seo_title || p.title} | Arcade Ops`, description: p.seo_description || p.excerpt, canonical: `${SITE_URL}/blog/${p.slug}`, type: "article" };
  }
  const st = /^\/states\/([a-z0-9-]+)$/.exec(path);
  if (st) {
    const name = st[1].split("-").map((w) => (w === "of" ? w : w.charAt(0).toUpperCase() + w.slice(1))).join(" ");
    return { title: `Skill Game Machines & Gaming Machines for C-Stores in ${name} — Verified Venue Owners | Arcade Ops`,
      description: `Place skill game machines, sweepstakes game kiosks and ATM kiosks in ${name}: verified owner contacts for c-stores, gas stations, bars and smoke shops plus approve-before-send calls and email for route operators. $399/mo per state, 14-day trial (card required).`,
      canonical: SITE_URL + path };
  }
  const camp = /^\/c\/([^/]+)$/.exec(path);
  if (camp) return { title: BRAND, canonical: `${SITE_URL}/${camp[1]}` };
  if (NOINDEX_PREFIXES.some((p) => path === p || path.startsWith(p.endsWith("/") ? p : p + "/"))) {
    return { title: `${BRAND} — Workspace`, noindex: true, canonical: SITE_URL + path };
  }
  return { title: BRAND, canonical: SITE_URL + path };
}

function setMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith("link") ? "link" : "meta");
    Object.entries(attrs).forEach(([k, v]) => k !== "content" && k !== "href" && el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  if (attrs.content !== undefined) el.setAttribute("content", attrs.content);
  if (attrs.href !== undefined) el.setAttribute("href", attrs.href);
}

export function applyMeta({ title, description, canonical, noindex, image, type }) {
  document.title = title || BRAND;
  const desc = description || PAGES["/"].description;
  setMeta('meta[name="description"]', { name: "description", content: desc });
  setMeta('meta[name="robots"]', { name: "robots", content: noindex ? "noindex, nofollow" : "index, follow" });
  setMeta('link[rel="canonical"]', { rel: "canonical", href: canonical || SITE_URL + window.location.pathname });
  setMeta('meta[property="og:title"]', { property: "og:title", content: document.title });
  setMeta('meta[property="og:description"]', { property: "og:description", content: desc });
  setMeta('meta[property="og:url"]', { property: "og:url", content: canonical || SITE_URL + window.location.pathname });
  setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: document.title });
  setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: desc });
  const og = image || `${SITE_URL}/api/public/og/home.png?v=20261007`;
  setMeta('meta[property="og:image"]', { property: "og:image", content: og });
  setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: og });
  setMeta('meta[property="og:type"]', { property: "og:type", content: type || "website" });
  const ld = jsonLdFor(window.location.pathname);
  let script = document.head.querySelector("script[data-route-jsonld]");
  if (ld) {
    if (!script) { script = document.createElement("script"); script.type = "application/ld+json"; script.setAttribute("data-route-jsonld", "1"); document.head.appendChild(script); }
    script.textContent = JSON.stringify(ld);
  } else if (script) script.remove();
}
